import * as XLSX from 'xlsx';

const formatDate = (dateVal) => {
  if (!dateVal) return '-';
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return String(dateVal);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getStepStatus = (steps = [], stepNum) => {
  if (!Array.isArray(steps)) return '-';
  const s = steps.find((item) => item.step === stepNum);
  if (!s) return '-';
  if (s.status === 'submitted') return 'Completed';
  if (s.status === 'in-progress') return 'In-Progress';
  if (s.status === 'pending') return 'Pending';
  return s.status || '-';
};

const getOverallStatus = (steps = []) => {
  if (!Array.isArray(steps) || steps.length === 0) return 'Pending';
  const allCompleted = steps.every((s) => s.status === 'submitted');
  if (allCompleted) return 'Completed';
  const anyInProgress = steps.some((s) => s.status === 'in-progress' || s.status === 'submitted');
  if (anyInProgress) return 'In-Progress';
  return 'Pending';
};

export const exportClientServicesToExcel = (
  clients = [],
  employeesMap = {},
  options = {}
) => {
  const {
    filename = '',
    selectedServices = {
      tax: true,
      annualReport: true,
      registeredAgent: true,
      bookkeeping: true,
      salesTax: true,
      usa: true,
      cfo: true,
      general: true,
    },
    includeMasterSheet = true,
    includeSeparateSheets = true,
  } = options;

  if (!Array.isArray(clients) || clients.length === 0) {
    alert('No client data available to export.');
    return;
  }

  // Count active selected services
  const activeServiceKeys = Object.keys(selectedServices).filter((k) => selectedServices[k]);
  const isSingleServiceSelected = activeServiceKeys.length === 1;

  const taxRows = [];
  const arRows = [];
  const raRows = [];
  const bkRows = [];
  const salesTaxRows = [];
  const usaRows = [];
  const cfoRows = [];
  const generalRows = [];

  clients.forEach((user) => {
    const empName =
      employeesMap[user._id] ||
      (user.employeeId ? (user.employeeId.name || user.employeeId) : null) ||
      'Unassigned';

    const birthDate = formatDate(user.dateOfBirth);
    let hasAnyService = false;

    // ── 1. TAX FILING ──
    if (selectedServices.tax && (user.isTaxFilingEnabled || user.assignedForm || user.dueDate || user.taxYearEndDate)) {
      hasAnyService = true;
      const primaryRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Tax Form': user.assignedForm || '-',
        'Tax Year End Date': formatDate(user.taxYearEndDate),
        'Tax Due Date': formatDate(user.dueDate),
        'Filing Date': formatDate(user.filingDate || user.step4CompletionDate),
        'Overall Status': getOverallStatus(user.steps),
        'Step 1 (Questionnaire)': getStepStatus(user.steps, 1),
        'Step 2 (Documents)': getStepStatus(user.steps, 2),
        'Step 3 (Draft Table)': getStepStatus(user.steps, 3),
        'Step 4 (Final Return)': getStepStatus(user.steps, 4),
        'Payment Status': user.paymentStatus || 'Unpaid',
        '1-Month Reminder': user.oneMonthReminderSent ? 'Yes' : 'No',
        '15-Day Reminder': user.fifteenDayReminderSent ? 'Yes' : 'No',
        'Due Date Reminder': user.dueDateReminderSent ? 'Yes' : 'No',
        'Created At': formatDate(user.createdAt),
      };
      taxRows.push(primaryRow);

      if (Array.isArray(user.members) && user.members.length > 0) {
        user.members.forEach((m, mIdx) => {
          const memberRow = {
            'Client ID': String(user._id),
            'Client Name': user.name || '',
            'Birthdate': birthDate,
            'Email': user.email || '',
            'Phone': user.phone || user.mobile || '-',
            'Organization / Entity': m.name || `Member #${mIdx + 1}`,
            'Assigned Employee': empName,
            'Tax Form': m.assignedForm || user.assignedForm || '-',
            'Tax Year End Date': formatDate(m.taxYearEndDate || user.taxYearEndDate),
            'Tax Due Date': formatDate(m.dueDate || user.dueDate),
            'Filing Date': formatDate(m.filingDate || m.step4CompletionDate),
            'Overall Status': getOverallStatus(m.steps),
            'Step 1 (Questionnaire)': getStepStatus(m.steps, 1),
            'Step 2 (Documents)': getStepStatus(m.steps, 2),
            'Step 3 (Draft Table)': getStepStatus(m.steps, 3),
            'Step 4 (Final Return)': getStepStatus(m.steps, 4),
            'Payment Status': m.paymentStatus || user.paymentStatus || 'Unpaid',
            '1-Month Reminder': m.oneMonthReminderSent ? 'Yes' : 'No',
            '15-Day Reminder': m.fifteenDayReminderSent ? 'Yes' : 'No',
            'Due Date Reminder': m.dueDateReminderSent ? 'Yes' : 'No',
            'Created At': formatDate(user.createdAt),
          };
          taxRows.push(memberRow);
        });
      }
    }

    // ── 2. ANNUAL REPORT ──
    if (selectedServices.annualReport && (user.isAnnualReportServiceEnabled || user.annualReportService?.dueDate || user.annualReportService?.filingYear)) {
      hasAnyService = true;
      const arRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Entity Type': user.annualReportService?.entityType || 'LLC',
        'State': user.annualReportService?.state || '-',
        'Filing Year': user.annualReportService?.filingYear ? String(user.annualReportService.filingYear) : '-',
        'Annual Report Due Date': formatDate(user.annualReportService?.dueDate),
        'Filing / Registration Date': formatDate(user.annualReportService?.registrationDate),
        'Service Status': user.isAnnualReportServiceEnabled ? 'Active' : 'Pending',
        'Payment Status': user.paymentStatus || 'Unpaid',
        '1-Month Reminder': user.annualReportService?.oneMonthReminderSent ? 'Yes' : 'No',
        '15-Day Reminder': user.annualReportService?.fifteenDayReminderSent ? 'Yes' : 'No',
        'Due Date Reminder': user.annualReportService?.dueDateReminderSent ? 'Yes' : 'No',
        'Created At': formatDate(user.createdAt),
      };
      arRows.push(arRow);

      if (Array.isArray(user.annualReportHistory) && user.annualReportHistory.length > 0) {
        user.annualReportHistory.forEach((hist) => {
          const histRow = {
            'Client ID': String(user._id),
            'Client Name': user.name || '',
            'Birthdate': birthDate,
            'Email': user.email || '',
            'Phone': user.phone || user.mobile || '-',
            'Organization / Entity': user.organization || '-',
            'Assigned Employee': empName,
            'Entity Type': hist.entityType || user.annualReportService?.entityType || 'LLC',
            'State': hist.state || user.annualReportService?.state || '-',
            'Filing Year': hist.filingYear ? String(hist.filingYear) : '-',
            'Annual Report Due Date': formatDate(hist.dueDate),
            'Filing / Registration Date': formatDate(hist.completedAt || hist.registrationDate),
            'Service Status': 'Completed History',
            'Payment Status': 'Paid',
            '1-Month Reminder': '-',
            '15-Day Reminder': '-',
            'Due Date Reminder': '-',
            'Created At': formatDate(user.createdAt),
          };
          arRows.push(histRow);
        });
      }
    }

    // ── 3. REGISTERED AGENT ──
    if (selectedServices.registeredAgent && (user.isRegisteredAgentEnabled || user.registeredAgentService?.dueDate || user.registeredAgentService?.dueDate2)) {
      hasAnyService = true;
      if (user.registeredAgentService?.dueDate || user.isRegisteredAgentEnabled) {
        const raRow = {
          'Client ID': String(user._id),
          'Client Name': user.name || '',
          'Birthdate': birthDate,
          'Email': user.email || '',
          'Phone': user.phone || user.mobile || '-',
          'Organization / Entity': user.organization || '-',
          'Assigned Employee': empName,
          'Slot': 'Service 1',
          'State': user.registeredAgentService?.state || '-',
          'Service Due Date': formatDate(user.registeredAgentService?.dueDate),
          'Service Status': user.isRegisteredAgentEnabled ? 'Active' : 'Pending',
          'Payment Status': user.paymentStatus || 'Unpaid',
          '1-Month Reminder': user.registeredAgentService?.oneMonthReminderSent ? 'Yes' : 'No',
          '15-Day Reminder': user.registeredAgentService?.fifteenDayReminderSent ? 'Yes' : 'No',
          '5-Day Reminder': user.registeredAgentService?.fiveDayReminderSent ? 'Yes' : 'No',
          'Created At': formatDate(user.createdAt),
        };
        raRows.push(raRow);
      }
      if (user.registeredAgentService?.dueDate2) {
        const raRow2 = {
          'Client ID': String(user._id),
          'Client Name': user.name || '',
          'Birthdate': birthDate,
          'Email': user.email || '',
          'Phone': user.phone || user.mobile || '-',
          'Organization / Entity': user.organization || '-',
          'Assigned Employee': empName,
          'Slot': 'Service 2',
          'State': user.registeredAgentService?.state2 || '-',
          'Service Due Date': formatDate(user.registeredAgentService?.dueDate2),
          'Service Status': user.isRegisteredAgentEnabled ? 'Active' : 'Pending',
          'Payment Status': user.paymentStatus || 'Unpaid',
          '1-Month Reminder': user.registeredAgentService?.oneMonthReminderSent2 ? 'Yes' : 'No',
          '15-Day Reminder': user.registeredAgentService?.fifteenDayReminderSent2 ? 'Yes' : 'No',
          '5-Day Reminder': user.registeredAgentService?.fiveDayReminderSent2 ? 'Yes' : 'No',
          'Created At': formatDate(user.createdAt),
        };
        raRows.push(raRow2);
      }
    }

    // ── 4. BOOKKEEPING ──
    if (selectedServices.bookkeeping && (user.isBookkeepingEnabled || user.bookkeepingService?.dueDate || user.bookkeepingService?.startDate)) {
      hasAnyService = true;
      const bkRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Frequency': user.bookkeepingService?.frequency || 'Monthly',
        'Start Date': formatDate(user.bookkeepingService?.startDate),
        'Due Date': user.bookkeepingService?.dueDateText || formatDate(user.bookkeepingService?.dueDate),
        'Overall Status': getOverallStatus(user.bookkeepingSteps),
        'Step 1': getStepStatus(user.bookkeepingSteps, 1),
        'Step 2': getStepStatus(user.bookkeepingSteps, 2),
        'Step 3': getStepStatus(user.bookkeepingSteps, 3),
        'Step 4': getStepStatus(user.bookkeepingSteps, 4),
        'Payment Status': user.paymentStatus || '-',
        'Created At': formatDate(user.createdAt),
      };
      bkRows.push(bkRow);
    }

    // ── 5. SALES TAX MODULE ──
    if (selectedServices.salesTax && (user.isSalesTaxEnabled || user.salesTaxService?.dueDate || user.salesTaxService?.state)) {
      hasAnyService = true;
      const stRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'State': user.salesTaxService?.state || '-',
        'Entity Type': user.salesTaxService?.entityType || 'LLC',
        'Frequency': user.salesTaxService?.frequency || 'Quarterly',
        'Period': user.salesTaxService?.periodText || '-',
        'Sales Tax Due Date': user.salesTaxService?.dueDateText || formatDate(user.salesTaxService?.dueDate),
        'Service Status': user.isSalesTaxEnabled ? 'Active' : 'Pending',
        'Overall Status': getOverallStatus(user.salesTaxSteps),
        'Step 1 (Checklist)': getStepStatus(user.salesTaxSteps, 1),
        'Step 2 (Documents)': getStepStatus(user.salesTaxSteps, 2),
        'Step 3 (Return)': getStepStatus(user.salesTaxSteps, 3),
        'Payment Status': user.paymentStatus || '-',
        'Email Status': user.salesTaxService?.emailStatus || 'Pending',
        'Created At': formatDate(user.createdAt),
      };
      salesTaxRows.push(stRow);
    }

    // ── 6. USA COMPANY FORMATION ──
    if (selectedServices.usa && (user.isUSAEnabled || user.assignedCompanyForm || (user.companySteps && user.companySteps.length > 0))) {
      hasAnyService = true;
      const usaRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Company Form': user.assignedCompanyForm || '-',
        'Formation Date / Due Date': formatDate(user.usaCompanyFormation?.createdAt || user.createdAt),
        'Overall Status': getOverallStatus(user.companySteps),
        'Step 1 (Details)': getStepStatus(user.companySteps, 1),
        'Step 2 (Members)': getStepStatus(user.companySteps, 2),
        'Step 3 (EIN & State)': getStepStatus(user.companySteps, 3),
        'Step 4 (Final Docs)': getStepStatus(user.companySteps, 4),
        'Payment Status': user.paymentStatus || '-',
        'Created At': formatDate(user.createdAt),
      };
      usaRows.push(usaRow);
    }

    // ── 7. CFO ADVISORY ──
    if (selectedServices.cfo && user.isCFOEnabled) {
      hasAnyService = true;
      const cfoRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Service Status': 'Active',
        'Payment Status': user.paymentStatus || '-',
        'Created At': formatDate(user.createdAt),
      };
      cfoRows.push(cfoRow);
    }

    // ── 8. GENERAL ACCOUNTS ──
    if (selectedServices.general && !hasAnyService) {
      const fallbackRow = {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': birthDate,
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Account Status': 'Registered Client',
        'Payment Status': user.paymentStatus || '-',
        'Created At': formatDate(user.createdAt),
      };
      generalRows.push(fallbackRow);
    }
  });

  const wb = XLSX.utils.book_new();

  // Single service mode (e.g., ONLY Annual Report or Sales Tax is selected)
  if (isSingleServiceSelected) {
    const singleKey = activeServiceKeys[0];
    let rowsToExport = [];
    let sheetName = 'Service Report';

    if (singleKey === 'annualReport') {
      rowsToExport = arRows;
      sheetName = 'Annual Report';
    } else if (singleKey === 'tax') {
      rowsToExport = taxRows;
      sheetName = 'Tax Filing';
    } else if (singleKey === 'registeredAgent') {
      rowsToExport = raRows;
      sheetName = 'Registered Agent';
    } else if (singleKey === 'bookkeeping') {
      rowsToExport = bkRows;
      sheetName = 'Bookkeeping';
    } else if (singleKey === 'salesTax') {
      rowsToExport = salesTaxRows;
      sheetName = 'Sales Tax';
    } else if (singleKey === 'usa') {
      rowsToExport = usaRows;
      sheetName = 'USA Formation';
    } else if (singleKey === 'cfo') {
      rowsToExport = cfoRows;
      sheetName = 'CFO Advisory';
    } else if (singleKey === 'general') {
      rowsToExport = generalRows;
      sheetName = 'General Accounts';
    }

    if (rowsToExport.length === 0) {
      alert(`No records found for ${sheetName}.`);
      return;
    }

    const wsSingle = XLSX.utils.json_to_sheet(rowsToExport);
    XLSX.utils.book_append_sheet(wb, wsSingle, sheetName);
  } else {
    // ── Multiple services selected ──

    // 1. Master Sheet: Client Services Due Dates Summary
    const dueDatesSummaryRows = clients.map((user) => {
      const empName =
        employeesMap[user._id] ||
        (user.employeeId ? (user.employeeId.name || user.employeeId) : null) ||
        'Unassigned';
      const bk = user.bookkeepingService || {};
      const ar = user.annualReportService || {};
      const ra = user.registeredAgentService || {};
      const st = user.salesTaxService || {};

      const activeList = [
        user.isUSAEnabled && 'USA Company Formation',
        user.isTaxFilingEnabled && 'Tax Filing',
        user.isBookkeepingEnabled && 'Bookkeeping',
        user.isCFOEnabled && 'CFO Advisory',
        user.isRegisteredAgentEnabled && 'Registered Agent',
        user.isAnnualReportServiceEnabled && 'Annual Report',
        user.isSalesTaxEnabled && 'Sales Tax',
      ].filter(Boolean);

      return {
        'Client ID': String(user._id),
        'Client Name': user.name || '',
        'Birthdate': formatDate(user.dateOfBirth),
        'Email': user.email || '',
        'Phone': user.phone || user.mobile || '-',
        'Organization / Entity': user.organization || '-',
        'Assigned Employee': empName,
        'Active Services': activeList.length ? activeList.join(', ') : 'None',
        'Tax Filing Due Date': (user.isTaxFilingEnabled || user.dueDate) ? formatDate(user.dueDate) : '-',
        'Bookkeeping Due Date': (user.isBookkeepingEnabled || bk.dueDate) ? (bk.dueDateText || formatDate(bk.dueDate)) : '-',
        'Annual Report Due Date': (user.isAnnualReportServiceEnabled || ar.dueDate) ? (ar.dueDateText || formatDate(ar.dueDate)) : '-',
        'Registered Agent Due Date (1)': ra.dueDate ? formatDate(ra.dueDate) : '-',
        'Registered Agent Due Date (2)': ra.dueDate2 ? formatDate(ra.dueDate2) : '-',
        'Sales Tax Due Date': (user.isSalesTaxEnabled || st.dueDate) ? (st.dueDateText || formatDate(st.dueDate)) : '-',
        'USA Formation Due Date': user.isUSAEnabled ? formatDate(user.usaCompanyFormation?.createdAt || user.createdAt) : '-',
        'Created At': formatDate(user.createdAt),
      };
    });

    if (dueDatesSummaryRows.length > 0) {
      const wsSummary = XLSX.utils.json_to_sheet(dueDatesSummaryRows);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Client Due Dates Summary');
    }

    if (includeMasterSheet) {
      const allCombinedRows = [
        ...taxRows,
        ...arRows,
        ...raRows,
        ...bkRows,
        ...salesTaxRows,
        ...usaRows,
        ...cfoRows,
        ...generalRows,
      ];
      if (allCombinedRows.length > 0) {
        const wsMaster = XLSX.utils.json_to_sheet(allCombinedRows);
        XLSX.utils.book_append_sheet(wb, wsMaster, 'All Selected Services');
      }
    }

    if (includeSeparateSheets) {
      if (selectedServices.annualReport && arRows.length > 0) {
        const wsAR = XLSX.utils.json_to_sheet(arRows);
        XLSX.utils.book_append_sheet(wb, wsAR, 'Annual Report');
      }

      if (selectedServices.tax && taxRows.length > 0) {
        const wsTax = XLSX.utils.json_to_sheet(taxRows);
        XLSX.utils.book_append_sheet(wb, wsTax, 'Tax Filing');
      }

      if (selectedServices.registeredAgent && raRows.length > 0) {
        const wsRA = XLSX.utils.json_to_sheet(raRows);
        XLSX.utils.book_append_sheet(wb, wsRA, 'Registered Agent');
      }

      if (selectedServices.bookkeeping && bkRows.length > 0) {
        const wsBK = XLSX.utils.json_to_sheet(bkRows);
        XLSX.utils.book_append_sheet(wb, wsBK, 'Bookkeeping');
      }

      if (selectedServices.salesTax && salesTaxRows.length > 0) {
        const wsST = XLSX.utils.json_to_sheet(salesTaxRows);
        XLSX.utils.book_append_sheet(wb, wsST, 'Sales Tax');
      }

      if (selectedServices.usa && usaRows.length > 0) {
        const wsUSA = XLSX.utils.json_to_sheet(usaRows);
        XLSX.utils.book_append_sheet(wb, wsUSA, 'USA Formation');
      }

      if (selectedServices.cfo && cfoRows.length > 0) {
        const wsCFO = XLSX.utils.json_to_sheet(cfoRows);
        XLSX.utils.book_append_sheet(wb, wsCFO, 'CFO Advisory');
      }

      if (selectedServices.general && generalRows.length > 0) {
        const wsGen = XLSX.utils.json_to_sheet(generalRows);
        XLSX.utils.book_append_sheet(wb, wsGen, 'General Accounts');
      }
    }
  }

  if (wb.SheetNames.length === 0) {
    alert('No data found for the selected options.');
    return;
  }

  const dateStr = new Date().toISOString().slice(0, 10);
  const finalFilename = filename || `Client_Services_${dateStr}.xlsx`;

  XLSX.writeFile(wb, finalFilename);
};
